import { Button, ButtonGroup, Stack, Typography } from '@mui/material';
import { type JSX, useEffect, useState } from 'react';

import GenericDialog from 'portal/src/components/common/GenericDialog';
import { FormCard } from 'portal/src/components/common/forms/FormCard';

import AllocationLineItems from './AllocationLineItems';
import AllocationLineItemsDialogMessage from './AllocationLineItemsDialogMessage';
import AllocationAccounts from './AllocationAccounts';

import { useBillFormContext } from '../BillFormContext';

export default function CalculateAllocationsDialog({
  billId,
  lineItemTotalDoesNotMatchAmount,
  onClose,
  onGenerateAllocations,
  open,
}: {
  billId: string;
  lineItemTotalDoesNotMatchAmount: boolean;
  onClose: () => void;
  onGenerateAllocations: () => void;
  open: boolean;
}): JSX.Element {
  const { fetchAllocationPreview } = useBillFormContext();
  const [showAccountDetails, setShowAccountDetails] = useState<boolean>(true);

  useEffect(() => {
    if (open && !lineItemTotalDoesNotMatchAmount) {
      fetchAllocationPreview();
    }
  }, [fetchAllocationPreview, lineItemTotalDoesNotMatchAmount, open]);

  const renderDetails = () => {
    if (showAccountDetails) {
      return <AllocationAccounts />;
    }

    return <AllocationLineItems disabled={lineItemTotalDoesNotMatchAmount} />;
  };

  return (
    <GenericDialog
      dialogActions={
        <>
          {!!billId && (
            <Button
              color="secondary"
              disabled={lineItemTotalDoesNotMatchAmount}
              variant="contained"
              disableElevation
              onClick={onGenerateAllocations}
            >
              Generate allocations
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </>
      }
      maxWidth={'xxl'}
      open={open}
      onClose={onClose}
      title="Calculated Allocations"
    >
      <FormCard
        header={
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography>The bill will be allocated as follows:</Typography>
            <ButtonGroup>
              <Button
                variant={showAccountDetails ? 'contained' : 'outlined'}
                onClick={() => setShowAccountDetails(true)}
              >
                View By Account
              </Button>
              <Button
                variant={!showAccountDetails ? 'contained' : 'outlined'}
                onClick={() => setShowAccountDetails(false)}
              >
                View By Line Item
              </Button>
            </ButtonGroup>
          </Stack>
        }
        showContentDivider={false}
      >
        {renderDetails()}
      </FormCard>
      {lineItemTotalDoesNotMatchAmount && (
        <AllocationLineItemsDialogMessage message="Unable to generate allocations because the line item total does not match the bill amount." />
      )}
    </GenericDialog>
  );
}
